import { useEffect } from "react";
import { FieldPath, FieldValues, UseFormReturn } from "react-hook-form";

import Storage from "@utils/storageUtils";

export type FormPersistConfig<FormData extends FieldValues> = {
  dirty?: boolean;
  exclude?: string[];
  form: UseFormReturn<FormData>;
  onDataRestored?: (data: any) => void;
  touch?: boolean;
  uniqueId: string;
  validate?: boolean;
};

const useFormPersist = <FormData extends FieldValues>(
  name: string,
  {
    dirty = true,
    exclude = [],
    form,
    onDataRestored,
    touch = true,
    uniqueId,
    validate = true
  }: FormPersistConfig<FormData>
) => {
  const watchedValues = form.watch();
  const timeout = 24 * 60 * 60 * 1000; // time out after 24h
  const keyPrefix = "RHFPERSISTED";

  const storageKey = `${keyPrefix} ${uniqueId} ${name}`;

  useEffect(() => {
    Storage.get(storageKey).then(storedValue => {
      if (storedValue) {
        const { timestamp = null, ...values } = JSON.parse(storedValue);
        const dataRestored: { [key: string]: any } = {};
        const currTimestamp = Date.now();

        if (timeout && currTimestamp - timestamp > timeout) {
          Storage.remove(storageKey);
          return;
        }

        let setAny = false;

        // This is a bit yucky. It's pretty straightforward for standard fields, but for ones that use useFieldArray
        // it gets pretty messy. In that case we get an object for the value and need to manipulate the keys to
        // be what react-hook-form expects. I should maybe recurse on this but I don't want to. :)
        Object.keys(values).forEach((rawKey: string) => {
          const key = rawKey as FieldPath<FormData>;
          const shouldSet = !exclude.includes(key) && values[key] !== "";
          if (shouldSet) {
            if (typeof values[key] === "object" && values[key] !== null) {
              const objectValue = values[key] as { [key: string]: { value: string } };
              Object.keys(objectValue).forEach(objectKey => {
                const setterKey = `${key}.${objectKey}.value`;
                dataRestored[setterKey] = objectValue[objectKey].value;
                form.setValue(setterKey as FieldPath<FormData>, objectValue[objectKey].value as any, {
                  shouldDirty: dirty,
                  shouldTouch: touch,
                  shouldValidate: validate
                });
              });
            } else {
              dataRestored[key] = values[key];
              form.setValue(key, values[key], {
                shouldDirty: dirty,
                shouldTouch: touch,
                shouldValidate: validate
              });
            }
            setAny = true;
          }
        });

        if (setAny && onDataRestored) {
          onDataRestored(dataRestored);
        }
      }
    });
    // This needs to only get fired on the initial load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let values: { [key: string]: any } = {};

    if (exclude.length) {
      values = Object.entries(watchedValues)
        .filter(([key]) => !exclude.includes(key))
        .reduce((obj, [key, val]) => Object.assign(obj, { [key]: val }), {});
    } else {
      values = Object.assign({}, watchedValues);
    }

    // This value is the empty value for rich text areas. We don't want to reload from that.
    Object.keys(values).forEach(key => {
      if (values[key] === "<p></p>") {
        delete values[key];
      }
    });

    if (Object.entries(values).length) {
      values.timestamp = Date.now();
      Storage.set(storageKey, JSON.stringify(values));
    }
  }, [watchedValues, timeout, exclude, storageKey]);

  // This scans the existing keys in storage to see if there are any others that have timed out. We want to proactively
  // delete these since otherwise we could really fill up the storage. If this starts being used more, we will need to
  // be really thoughtful about maybe being even more proactive about clearing it, having a shorter timeout, or using
  // a different storage mechanism that can handle more data. We could also consider turning this off on native mobile.
  useEffect(() => {
    Storage.keys().then(keysResult => {
      keysResult.keys.forEach((key: string) => {
        const regex = new RegExp(`^${keyPrefix}`);
        if (key.match(regex)) {
          Storage.get(key).then(storedValue => {
            if (storedValue) {
              const { timestamp = null } = JSON.parse(storedValue);

              if (timestamp) {
                const currTimestamp = Date.now();

                if (timeout && currTimestamp - timestamp > timeout) {
                  Storage.remove(key);
                  return;
                }
              }
            }
          });
        }
      });
    });
  }, [timeout]);

  return {
    clear: () => Storage.remove(storageKey)
  };
};

export default useFormPersist;
